import React from 'react';

const TAGLINES = [
  'Chef is cooking up something special ✨',
  'Tossing the ingredients just right 🍳',
  'Almost ready to plate up 🍽️',
  'Fresh flavours incoming 🌿',
  'Sizzle, flip, serve! 🔥',
];

/**
 * Simulated progress hook — ramps up quickly then slows near the end.
 * Uses a setInterval (5 updates/sec) instead of rAF (60/sec) to avoid
 * constant React re-renders that cause frame drops.
 */
const useSimulatedProgress = () => {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const start = performance.now();
    const id = setInterval(() => {
      const elapsed = (performance.now() - start) / 1000;
      const pct = Math.min(99, 99 * (1 - Math.exp(-elapsed / 4)));
      setProgress(Math.round(pct));
    }, 200);
    return () => clearInterval(id);
  }, []);

  return progress;
};

/**
 * MealPrepLoader — Chef shaking a pan with veggies flying.
 *
 * Animated scene: A chef character holds/shakes a frying pan over flames.
 * Vegetables (carrot, broccoli, tomato, mushroom) launch upward on each shake,
 * arc through the air, then land back. Steam, sizzle sparks, and flame flicker
 * bring the scene alive. A rotating tagline adds personality.
 */
const MealPrepLoader = ({ size = 'md', label, inline = false }) => {
  const dims = { sm: 140, md: 200, lg: 260 }[size] || 200;
  const barWidth = Math.min(dims + 20, 240);
  const [tagIdx] = React.useState(() => Math.floor(Math.random() * TAGLINES.length));
  const txt = label ?? TAGLINES[tagIdx];
  const progress = useSimulatedProgress();

  return (
    <div
      className={
        inline
          ? 'flex flex-col items-center justify-center py-8'
          : 'fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm'
      }
      style={{ animation: 'mplEnter .45s cubic-bezier(.4,0,.2,1) both' }}
    >
      <div className="relative" style={{ width: dims, height: dims * 0.92 }}>
        {/* Warm ambient glow */}
        <div className="absolute inset-[-18%] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(249,115,22,0.10) 0%, transparent 65%)',
          animation: 'mplGlow 3.5s ease-in-out infinite',
        }} />

        <svg viewBox="20 60 200 165" fill="none" xmlns="http://www.w3.org/2000/svg"
             className="w-full h-full relative z-10 select-none"
             style={{ filter: 'drop-shadow(0 4px 18px rgba(0,0,0,0.06))' }}>
          <defs>
            <linearGradient id="mplPan" x1="70" y1="148" x2="160" y2="175" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#475569" />
              <stop offset="40%" stopColor="#334155" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
            <linearGradient id="mplApron" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fef3c7" />
              <stop offset="100%" stopColor="#fde68a" />
            </linearGradient>
            <radialGradient id="mplFlmG">
              <stop offset="0%" stopColor="#f97316" stopOpacity=".35" />
              <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
            </radialGradient>
            <filter id="mplBl"><feGaussianBlur stdDeviation="2.5" /></filter>
          </defs>

          <style>{`
/* ── PAN SHAKE (chef + pan move together) ── */
@keyframes mplShake{
  0%,100%{transform:translate(0,0) rotate(0)}
  10%{transform:translate(2px,0) rotate(.5deg)}
  25%{transform:translate(-1px,-6px) rotate(-3deg)}
  40%{transform:translate(1px,-2px) rotate(.5deg)}
  55%{transform:translate(-2px,0) rotate(-.5deg)}
  65%{transform:translate(1px,-5px) rotate(-2.5deg)}
  80%{transform:translate(-1px,-1px) rotate(.3deg)}
}
/* ── VEGGIE TOSS (launch up, arc, land back) ── */
@keyframes mplV1{
  0%,5%{transform:translate(0,0) rotate(0) scale(1);opacity:1}
  25%{transform:translate(-14px,-48px) rotate(-60deg) scale(1.15);opacity:1}
  50%{transform:translate(-4px,-60px) rotate(-120deg) scale(1.1);opacity:1}
  75%{transform:translate(8px,-38px) rotate(-200deg) scale(1.05);opacity:1}
  95%,100%{transform:translate(0,0) rotate(-360deg) scale(1);opacity:1}
}
@keyframes mplV2{
  0%,8%{transform:translate(0,0) rotate(0) scale(1);opacity:1}
  30%{transform:translate(12px,-52px) rotate(70deg) scale(1.18);opacity:1}
  55%{transform:translate(6px,-64px) rotate(140deg) scale(1.12);opacity:1}
  80%{transform:translate(-6px,-32px) rotate(240deg) scale(1);opacity:1}
  95%,100%{transform:translate(0,0) rotate(360deg) scale(1);opacity:1}
}
@keyframes mplV3{
  0%,3%{transform:translate(0,0) rotate(0) scale(1);opacity:1}
  28%{transform:translate(-8px,-55px) rotate(-50deg) scale(1.12);opacity:1}
  52%{transform:translate(4px,-68px) rotate(-100deg) scale(1.08);opacity:1}
  78%{transform:translate(10px,-30px) rotate(-180deg) scale(1.02);opacity:1}
  95%,100%{transform:translate(0,0) rotate(-280deg) scale(1);opacity:1}
}
@keyframes mplV4{
  0%,10%{transform:translate(0,0) rotate(0) scale(1);opacity:1}
  32%{transform:translate(6px,-44px) rotate(45deg) scale(1.1);opacity:1}
  56%{transform:translate(-8px,-58px) rotate(110deg) scale(1.15);opacity:1}
  82%{transform:translate(-2px,-28px) rotate(200deg) scale(1);opacity:1}
  95%,100%{transform:translate(0,0) rotate(320deg) scale(1);opacity:1}
}
/* ── FLAMES ── */
@keyframes mplFl1{0%,100%{transform:scaleY(1) scaleX(1);opacity:.8}50%{transform:scaleY(1.3) scaleX(.85);opacity:1}}
@keyframes mplFl2{0%,100%{transform:scaleY(1.1) scaleX(.9);opacity:.75}50%{transform:scaleY(.85) scaleX(1.1);opacity:.95}}
@keyframes mplFl3{0%,100%{transform:scaleY(.9) scaleX(1);opacity:.7}50%{transform:scaleY(1.25) scaleX(.9);opacity:.9}}
@keyframes mplFlGlow{0%,100%{opacity:.25;transform:scale(1)}50%{opacity:.45;transform:scale(1.12)}}
/* ── STEAM ── */
@keyframes mplSt1{0%{opacity:0;transform:translate(0,0)}12%{opacity:.35}55%{opacity:.1;transform:translate(-6px,-30px)}100%{opacity:0;transform:translate(-4px,-50px)}}
@keyframes mplSt2{0%{opacity:0;transform:translate(0,0)}15%{opacity:.3}50%{opacity:.08;transform:translate(5px,-26px)}100%{opacity:0;transform:translate(4px,-44px)}}
@keyframes mplSt3{0%{opacity:0;transform:translate(0,0)}10%{opacity:.25}52%{opacity:.06;transform:translate(-3px,-24px)}100%{opacity:0;transform:translate(-1px,-40px)}}
/* ── SIZZLE SPARKS ── */
@keyframes mplSz1{0%,80%{opacity:0;transform:translate(0,0) scale(0)}85%{opacity:1;transform:translate(-6px,-4px) scale(1)}100%{opacity:0;transform:translate(-14px,-18px) scale(.3)}}
@keyframes mplSz2{0%,78%{opacity:0;transform:translate(0,0) scale(0)}84%{opacity:.9;transform:translate(8px,-5px) scale(1.1)}100%{opacity:0;transform:translate(16px,-20px) scale(.2)}}
@keyframes mplSz3{0%,82%{opacity:0;transform:translate(0,0) scale(0)}87%{opacity:.8;transform:translate(-3px,-6px) scale(.9)}100%{opacity:0;transform:translate(-8px,-22px) scale(.15)}}
@keyframes mplSz4{0%,76%{opacity:0;transform:translate(0,0) scale(0)}82%{opacity:.85;transform:translate(5px,-3px) scale(1)}100%{opacity:0;transform:translate(12px,-16px) scale(.2)}}
/* ── CHEF HEAD BOB ── */
@keyframes mplBob{0%,100%{transform:translateY(0)}30%{transform:translateY(-2px)}60%{transform:translateY(1px)}}
/* ── SPARKLE TWINKLE ── */
@keyframes mplTw{0%,100%{opacity:0;transform:scale(0)}30%{opacity:1;transform:scale(1.2)}60%{opacity:.5;transform:scale(.8)}100%{opacity:0;transform:scale(0)}}

.mpl-scene{animation:mplShake 1.8s cubic-bezier(.4,0,.2,1) infinite;transform-origin:120px 170px}
.mpl-v1{animation:mplV1 1.8s cubic-bezier(.3,0,.5,1) infinite;transform-origin:center}
.mpl-v2{animation:mplV2 1.8s cubic-bezier(.3,0,.5,1) .12s infinite;transform-origin:center}
.mpl-v3{animation:mplV3 1.8s cubic-bezier(.3,0,.5,1) .06s infinite;transform-origin:center}
.mpl-v4{animation:mplV4 1.8s cubic-bezier(.3,0,.5,1) .18s infinite;transform-origin:center}
.mpl-fl1{animation:mplFl1 .5s ease-in-out infinite;transform-origin:bottom center}
.mpl-fl2{animation:mplFl2 .55s ease-in-out .15s infinite;transform-origin:bottom center}
.mpl-fl3{animation:mplFl3 .6s ease-in-out .3s infinite;transform-origin:bottom center}
.mpl-flG{animation:mplFlGlow 1.4s ease-in-out infinite}
.mpl-st1{animation:mplSt1 2.8s ease-out infinite}
.mpl-st2{animation:mplSt2 3.2s ease-out .8s infinite}
.mpl-st3{animation:mplSt3 2.6s ease-out 1.6s infinite}
.mpl-sz1{animation:mplSz1 1.8s ease-out infinite}
.mpl-sz2{animation:mplSz2 1.8s ease-out infinite}
.mpl-sz3{animation:mplSz3 1.8s ease-out infinite}
.mpl-sz4{animation:mplSz4 1.8s ease-out infinite}
.mpl-bob{animation:mplBob 1.8s ease-in-out infinite}
.mpl-tw1{animation:mplTw 2.4s ease-in-out infinite}
.mpl-tw2{animation:mplTw 2.8s ease-in-out .6s infinite}
.mpl-tw3{animation:mplTw 2.6s ease-in-out 1.2s infinite}
.mpl-tw4{animation:mplTw 3s ease-in-out 1.8s infinite}
          `}</style>

          {/* ═══ GROUND SHADOW ═══ */}
          <ellipse cx="120" cy="210" rx="70" ry="6" fill="#64748b" opacity="0.09" filter="url(#mplBl)" />

          {/* ═══ FLAMES (behind pan) ═══ */}
          <circle cx="115" cy="190" r="24" fill="url(#mplFlmG)" className="mpl-flG" />
          <g transform="translate(95,175)">
            <path className="mpl-fl1" d="M8 20 Q6 12 10 6 Q14 0 16 6 Q20 12 18 20Z" fill="#f97316" opacity=".85" />
            <path className="mpl-fl1" d="M10 20 Q9 14 12 8 Q15 4 16 8 Q18 14 17 20Z" fill="#fbbf24" opacity=".5" />
          </g>
          <g transform="translate(110,173)">
            <path className="mpl-fl2" d="M6 22 Q4 14 8 7 Q12 0 14 7 Q18 14 16 22Z" fill="#f97316" opacity=".9" />
            <path className="mpl-fl2" d="M8 22 Q7 16 10 9 Q13 4 14 9 Q16 16 15 22Z" fill="#fde047" opacity=".45" />
          </g>
          <g transform="translate(126,176)">
            <path className="mpl-fl3" d="M7 18 Q5 11 9 5 Q12 0 14 5 Q17 11 15 18Z" fill="#f97316" opacity=".8" />
            <path className="mpl-fl3" d="M9 18 Q8 13 11 7 Q13 3 14 7 Q15 13 14 18Z" fill="#fbbf24" opacity=".5" />
          </g>

          {/* ═══ CHEF + PAN GROUP (shakes together) ═══ */}
          <g className="mpl-scene">

            {/* ─── CHEF BODY ─── */}
            {/* Legs */}
            <rect x="52" y="186" width="8" height="22" rx="3" fill="#1e293b" />
            <rect x="66" y="186" width="8" height="22" rx="3" fill="#1e293b" />
            {/* Shoes */}
            <ellipse cx="55" cy="208" rx="7" ry="3.5" fill="#0f172a" />
            <ellipse cx="71" cy="208" rx="7" ry="3.5" fill="#0f172a" />

            {/* Torso */}
            <path d="M46 140 Q46 130 63 126 Q80 130 80 140 L80 188 Q80 194 63 194 Q46 194 46 188Z" fill="white" />
            {/* Apron */}
            <path d="M50 148 L76 148 L78 188 Q78 193 63 193 Q48 193 48 188Z" fill="url(#mplApron)" />
            {/* Apron pocket */}
            <rect x="56" y="164" width="14" height="10" rx="2" fill="#fcd34d" opacity=".3" />
            {/* Apron string */}
            <path d="M50 148 Q46 150 44 148" stroke="#e5c888" strokeWidth=".8" fill="none" />
            <path d="M76 148 Q80 150 82 148" stroke="#e5c888" strokeWidth=".8" fill="none" />
            {/* Chef coat buttons */}
            <circle cx="63" cy="138" r="1.2" fill="#e2e8f0" />
            <circle cx="63" cy="145" r="1.2" fill="#e2e8f0" />

            {/* Left arm (holding pan) — extends right */}
            <path d="M80 136 Q92 136 112 148" stroke="white" strokeWidth="7" strokeLinecap="round" fill="none" />
            {/* Sleeve shadow */}
            <path d="M80 136 Q86 136 92 138" stroke="#e2e8f0" strokeWidth="7" strokeLinecap="round" fill="none" />
            {/* Hand */}
            <circle cx="112" cy="150" r="5" fill="#f5d0a9" />
            <circle cx="116" cy="149" r="2.5" fill="#f5d0a9" />

            {/* Right arm (gesture up) */}
            <path d="M46 136 Q36 130 30 118" stroke="white" strokeWidth="7" strokeLinecap="round" fill="none" />
            <path d="M46 136 Q42 134 38 130" stroke="#e2e8f0" strokeWidth="7" strokeLinecap="round" fill="none" />
            {/* Right hand */}
            <circle cx="29" cy="116" r="4.5" fill="#f5d0a9" />
            {/* Pointing finger */}
            <path d="M26 113 Q24 108 25 104" stroke="#f5d0a9" strokeWidth="2.5" strokeLinecap="round" fill="none" />

            {/* ─── HEAD ─── */}
            <g className="mpl-bob">
              {/* Neck */}
              <rect x="58" y="120" width="10" height="8" rx="3" fill="#f5d0a9" />
              {/* Head */}
              <ellipse cx="63" cy="110" rx="16" ry="18" fill="#f5d0a9" />
              {/* Eyes */}
              <circle cx="56" cy="108" r="2" fill="#1e293b" />
              <circle cx="70" cy="108" r="2" fill="#1e293b" />
              {/* Eye highlights */}
              <circle cx="55" cy="107" r=".7" fill="white" />
              <circle cx="69" cy="107" r=".7" fill="white" />
              {/* Happy smile */}
              <path d="M56 114 Q63 120 70 114" stroke="#c2410c" strokeWidth="1.5" fill="none" strokeLinecap="round" />
              {/* Rosy cheeks */}
              <circle cx="50" cy="113" r="3" fill="#fca5a5" opacity=".3" />
              <circle cx="76" cy="113" r="3" fill="#fca5a5" opacity=".3" />
              {/* Nose */}
              <ellipse cx="63" cy="112" rx="1.8" ry="1.2" fill="#e8b898" />

              {/* Chef hat */}
              <path d="M44 100 Q44 78 63 72 Q82 78 82 100 L80 102 Q63 98 46 102Z" fill="white" />
              <rect x="44" y="100" width="38" height="6" rx="2" fill="#f1f5f9" />
              {/* Hat puff */}
              <ellipse cx="53" cy="78" rx="10" ry="6" fill="white" />
              <ellipse cx="73" cy="80" rx="9" ry="5" fill="white" />
              <ellipse cx="63" cy="74" rx="11" ry="7" fill="white" />
            </g>

            {/* ─── FRYING PAN ─── */}
            {/* Handle */}
            <rect x="148" y="152" width="44" height="6" rx="3" fill="#475569" />
            <rect x="148" y="153.5" width="44" height="2.5" rx="1.2" fill="#64748b" opacity=".4" />
            {/* Handle end knob */}
            <circle cx="192" cy="155" r="4" fill="#334155" />
            <circle cx="192" cy="155" r="2" fill="#475569" />

            {/* Pan body */}
            <ellipse cx="120" cy="170" rx="40" ry="8" fill="url(#mplPan)" />
            <path d="M80 162 Q80 150 120 146 Q160 150 160 162" fill="#334155" />
            {/* Pan interior sheen */}
            <path d="M86 158 Q90 152 108 149 L98 160Z" fill="white" opacity=".06" />
            {/* Rim highlight */}
            <ellipse cx="120" cy="162" rx="40" ry="6" fill="none" stroke="#64748b" strokeWidth="1.5" />
            <ellipse cx="120" cy="161" rx="38" ry="5" fill="none" stroke="white" strokeWidth=".5" opacity=".15" />
            {/* Oil shimmer */}
            <ellipse cx="115" cy="161" rx="16" ry="3" fill="#fbbf24" opacity=".08" />
            <ellipse cx="128" cy="160" rx="10" ry="2.5" fill="#fbbf24" opacity=".06" />

            {/* ═══ FOOD IN PAN (tossing upward) ═══ */}
            {/* Carrot pieces */}
            <g className="mpl-v1">
              <ellipse cx="108" cy="156" rx="5" ry="3.5" fill="#fb923c" />
              <ellipse cx="108" cy="156" rx="3" ry="2" fill="#fdba74" opacity=".5" />
            </g>
            {/* Broccoli */}
            <g className="mpl-v2">
              <circle cx="126" cy="155" r="5.5" fill="#22c55e" />
              <circle cx="124" cy="153" r="2.2" fill="#4ade80" opacity=".65" />
              <circle cx="128" cy="153" r="2" fill="#86efac" opacity=".5" />
            </g>
            {/* Tomato wedge */}
            <g className="mpl-v3">
              <circle cx="116" cy="158" r="4.5" fill="#ef4444" />
              <ellipse cx="114" cy="156" rx="2" ry="1.2" fill="white" opacity=".18" />
            </g>
            {/* Mushroom */}
            <g className="mpl-v4">
              <path d="M132 160 Q136 152 140 160" fill="#c4875a" />
              <rect x="134.5" y="160" width="3" height="4" rx="1" fill="#fde68a" />
            </g>
          </g>

          {/* ═══ SIZZLE SPARKS (synced with shake) ═══ */}
          <g className="mpl-sz1"><circle cx="95" cy="166" r="1.8" fill="#fbbf24" /><circle cx="93" cy="164" r="1" fill="#fde047" /></g>
          <g className="mpl-sz2"><circle cx="145" cy="164" r="1.6" fill="#fb923c" /><circle cx="147" cy="162" r=".9" fill="#fde047" /></g>
          <g className="mpl-sz3"><circle cx="108" cy="160" r="1.3" fill="#fcd34d" /></g>
          <g className="mpl-sz4"><circle cx="138" cy="162" r="1.4" fill="#fdba74" /></g>

          {/* ═══ STEAM WISPS ═══ */}
          <path className="mpl-st1" d="M105 142 Q98 126 108 112 Q102 100 110 86" stroke="#a8a29e" strokeWidth="2.2" strokeLinecap="round" fill="none" opacity="0" />
          <path className="mpl-st2" d="M125 140 Q132 124 122 108 Q130 94 124 82" stroke="#a8a29e" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0" />
          <path className="mpl-st3" d="M140 144 Q135 130 142 116 Q137 104 144 92" stroke="#a8a29e" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0" />

          {/* ═══ SPARKLE TWINKLES ═══ */}
          <g className="mpl-tw1"><path d="M90 88 L92 84 L94 88 L98 90 L94 92 L92 96 L90 92 L86 90Z" fill="#fbbf24" opacity="0" /></g>
          <g className="mpl-tw2"><path d="M160 78 L161.5 75 L163 78 L166 79.5 L163 81 L161.5 84 L160 81 L157 79.5Z" fill="#fcd34d" opacity="0" /></g>
          <g className="mpl-tw3"><circle cx="80" cy="72" r="1.6" fill="#fde68a" opacity="0" /></g>
          <g className="mpl-tw4"><path d="M172 94 L173 92 L174 94 L176 95 L174 96 L173 98 L172 96 L170 95Z" fill="#fbbf24" opacity="0" /></g>
        </svg>
      </div>

      {/* ═══ TAGLINE + PROGRESS BAR ═══ */}
      <div className="mt-5 flex flex-col items-center gap-3 w-full" style={{ maxWidth: barWidth }}>
        {txt && (
          <span className="text-sm font-medium tracking-wide text-center"
                style={{ animation: 'mplFade 2.5s ease-in-out infinite', color: 'var(--color-muted-foreground, #78716c)' }}>
            {txt}
          </span>
        )}

        {/* Progress bar */}
        <div className="w-full">
          <div className="relative h-2.5 rounded-full overflow-hidden"
               style={{ background: 'var(--color-muted, #e7e5e4)' }}>
            <div className="absolute inset-y-0 left-0 rounded-full"
                 style={{
                   width: `${progress}%`,
                   background: 'linear-gradient(90deg, #f97316 0%, #f59e0b 40%, #22c55e 100%)',
                   transition: 'width 0.4s cubic-bezier(.4,0,.2,1)',
                   boxShadow: '0 0 8px rgba(249,115,22,0.35)',
                 }} />
            {/* Shimmer overlay */}
            <div className="absolute inset-0 rounded-full"
                 style={{
                   background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
                   animation: 'mplShimmer 1.8s ease-in-out infinite',
                 }} />
          </div>
          <div className="mt-1.5 text-center">
            <span className="text-xs font-semibold tabular-nums"
                  style={{ color: 'var(--color-muted-foreground, #78716c)' }}>
              {progress}%
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes mplEnter{from{opacity:0;transform:scale(.92) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes mplGlow{0%,100%{transform:scale(1);opacity:.5}50%{transform:scale(1.12);opacity:1}}
        @keyframes mplFade{0%,100%{opacity:.35}50%{opacity:1}}
        @keyframes mplShimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}
      `}</style>
    </div>
  );
};

export default MealPrepLoader;
